import { Injectable } from '@angular/core';
import { filter, map, Observable } from 'rxjs';
import { ObservablePropertyBag, UnitOfWorkBase } from '.';

export interface ComponentState<T> {
  getState(): T;
  setState(state: Partial<T>): void;
  setDefaultState(state: Partial<T>): void;
  readonly stateChanged$: Observable<T>;
}

class ObservablePropertyBagComponentState<T> implements ComponentState<T> {
  readonly stateChanged$: Observable<T>;

  private readonly nameRegExp = new RegExp(`^${this.getPrefix()}(.+)$`);

  constructor(private state: ObservablePropertyBag, private componentName: string, initialState: Partial<T>) {
    this.setDefaultState(initialState || {});

    this.stateChanged$ = state.changed$.pipe(
      filter((changes) => Object.keys(changes).some((name) => name.startsWith(this.getPrefix()))),
      map(() => this.getState())
    );
  }

  getState(): T {
    const state = this.state.snapshot;
    const keys = Object.keys(state).filter((key) => key.startsWith(this.getPrefix()));
    return keys
      .map((key) => ({ name: this.getName(key), value: state[key] }))
      .reduce((prev, curr) => {
        prev[curr.name] = curr.value;
        return prev;
      }, {} as T);
  }

  setState(state: Partial<T>): void {
    this.state.beginUpdate();
    try {
      Object.keys(state).forEach((name) => this.state.setValue(this.makeFullyQualifiedName(name), state[name]));
    } finally {
      this.state.endUpdate();
    }
  }

  setDefaultState(defaultState: Partial<T>): void {
    this.state.beginUpdate();
    try {
      Object.keys(defaultState).forEach((name) =>
        this.state.setDefaultValue(this.makeFullyQualifiedName(name), defaultState[name])
      );
    } finally {
      this.state.endUpdate();
    }
  }

  private makeFullyQualifiedName(name: string): string {
    return `${this.getPrefix()}${name}`;
  }

  private getName(qualifiedName: string): string {
    const match = qualifiedName.match(this.nameRegExp);
    return match[1];
  }

  private getPrefix(): string {
    return `${this.componentName}::`;
  }
}

@Injectable({ providedIn: 'root' })
export class ComponentStateService {
  private globalState = new ObservablePropertyBag();

  getComponentState<T>(
    componentName: string,
    source: 'global' | UnitOfWorkBase = 'global',
    initialState?: Partial<T>
  ): ComponentState<T> {
    const state = source === 'global' ? this.globalState : source.state;
    return new ObservablePropertyBagComponentState<T>(state, componentName, initialState);
  }
}
